import * as THREE from "three";

import type { TilePos } from "../world/types";

/*
 * The wireframe box around the tile under the crosshair.
 *
 * One `LineSegments` for the whole session, moved rather than rebuilt: a
 * highlight that allocated a geometry per frame would cost more than everything
 * else the frame does with the raycast that produced it. It is a unit cube at
 * the origin, positioned at the tile's corner, so the object's matrix is the
 * only thing that changes.
 *
 * Slightly inflated, because a wireframe exactly on the tile's faces z-fights
 * with the terrain it is drawn against — the classic decal problem, solved the
 * classic cheap way. `depthTest` stays on: a highlight that shone through the
 * hill in front of it would be worse than one that disappears behind it.
 */

/** How far the box is grown past the tile it marks, in tiles. */
const INFLATE = 0.004;

/** The highlight's colour and how visible it is. Chrome, not art (roadmap, Gate A). */
const COLOR = 0x101418;
const OPACITY = 0.55;

export class TileHighlight {
  private readonly lines: THREE.LineSegments;

  constructor(scene: THREE.Scene) {
    const box = new THREE.BoxGeometry(1 + INFLATE * 2, 1 + INFLATE * 2, 1 + INFLATE * 2);
    const material = new THREE.LineBasicMaterial({ color: COLOR, transparent: true, opacity: OPACITY });

    this.lines = new THREE.LineSegments(new THREE.EdgesGeometry(box), material);
    this.lines.name = "tile highlight";
    this.lines.matrixAutoUpdate = false;
    this.lines.visible = false;
    // The edges geometry is what is drawn; the box was only ever a source of
    // corners and is released as soon as it has provided them.
    box.dispose();

    scene.add(this.lines);
  }

  /** Puts the box around a tile. */
  show(tile: TilePos): void {
    // A tile occupies `[x, x+1)`, so its centre is half a tile past its corner.
    this.lines.position.set(tile.x + 0.5, tile.y + 0.5, tile.z + 0.5);
    this.lines.updateMatrix();
    this.lines.visible = true;
  }

  /** Hides the box: nothing is in range under the crosshair. */
  hide(): void {
    this.lines.visible = false;
  }
}
